import { useMemo } from 'react';
import { router } from '@inertiajs/react';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { autosaveStatusEmitter } from '@/hooks/use-autosave-status';
import {
  buildPagesTree,
  fetchAllPages,
  fetchIndexPageChildren,
  fetchLoadedTreeWindow,
  fetchTreePageBatch,
  flattenPagesTree,
  getEditorRedirectPathAfterPageDelete,
  type ReorderOperation,
  TREE_PAGE_DEFAULT_PER_PAGE,
} from '@/layouts/app/sidebar/page-tree-sidebar/page-utils';
import http, { extractApiErrorMessage } from '@/lib/http';
import { fetchPublishedPages } from '@/lib/pages';
import { readRecord, readString } from '@/lib/prop-utils';
import {
  cmsBillingPath,
  cmsPagesEditorIndexPath,
  cmsPagesEditorPath,
  cmsPagesEditorSchedulePath,
} from '@/lib/routes';
import { useAccountLimitsStore } from '@/stores/account-limits-store';
import { useNestedPageViewStore } from '@/stores/nested-page-view-store';
import {
  clearQueuedAutosave,
  flushPendingAutosave,
  getAutosaveSnapshot,
  setAutosaveContext,
} from '@/stores/page-editor-middleware';
import { usePageEditorStore } from '@/stores/page-editor-store';
import { usePlanLimitModalStore } from '@/stores/plan-limit-modal-store';
import { flushPendingSiteStylesSaves, useSiteStylesStore } from '@/stores/site-styles-store';
import type { AccountUsage } from '@/types/account-usage';
import type { IndexPagesExist, Page, PageVersionMetadata, SectionStructure } from '@/types/page';
import { EMPTY_INDEX_PAGES_EXIST, PageType } from '@/types/page';
import type { PaginationInfo } from '@/types/pagination';

interface CreatePageParams {
  pageType: string;
  pageTemplateCode?: string;
  sections?: SectionStructure[];
  metadata?: PageVersionMetadata;
  contentModel?: Record<string, unknown>;
}

interface CreatePageOptions {
  onSuccess?: () => void;
  onError?: (errors: unknown) => void;
  onFinish?: () => void;
}

interface PageTrees {
  mainNavigationTree: Page[];
  nonNavigationTree: Page[];
}

type PageTreeScope = 'navigation' | 'non_navigation';

interface PagesStore {
  mainNavigationFlat: Page[];
  nonNavigationFlat: Page[];
  mainNavigationPagination: PaginationInfo;
  nonNavigationPagination: PaginationInfo;
  publishedPages: Page[];
  indexPagesExist: IndexPagesExist;
  searchResults: PageTrees | null;
  isLoading: boolean;
  hasLoaded: boolean;
  latestPagesUpdatedAt: string | null;
  maxTreeDepth: number;
  maxPinnedPages: number;

  updatePage: (pageId: string, updates: Partial<Page>) => void;
  setSearchResults: (results: PageTrees | null) => void;

  createPage: (params: CreatePageParams, options?: CreatePageOptions) => Promise<void>;
  publishPage: (pageId: string) => Promise<boolean>;
  schedulePage: (
    pageId: string,
    scheduledPublishAt: string,
    options?: { updateExisting?: boolean },
  ) => Promise<void>;
  unschedulePage: (pageId: string) => Promise<void>;
  unpublishPage: (pageId: string) => Promise<void>;
  movePageToNonNavigation: (pageId: string) => Promise<void>;
  movePageToMainNavigation: (pageId: string) => Promise<void>;
  reorderPage: (params: ReorderOperation, nextTree?: Page[]) => Promise<void>;
  togglePin: (pageId: string, isPinned: boolean) => Promise<void>;
  deletePage: (pageId: string) => Promise<void>;

  loadPages: () => Promise<void>;
  loadMorePages: (scope: PageTreeScope) => Promise<void>;
  hasIndexPageType: (pageType: string) => boolean;
  reset: () => void;
}

const EMPTY_TREE_PAGINATION: PaginationInfo = {
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  perPage: TREE_PAGE_DEFAULT_PER_PAGE,
};

function mergePages(existing: Page[], incoming: Page[]): Page[] {
  const seen = new Set(existing.map((page) => page.id));
  const merged = [...existing];

  for (const page of incoming) {
    if (seen.has(page.id)) continue;
    seen.add(page.id);
    merged.push(page);
  }

  return merged;
}

let treeLoadRequestId = 0;

function nextTreeLoadRequestId(): number {
  treeLoadRequestId += 1;
  return treeLoadRequestId;
}

// Fallback defaults used only when the API call fails.
// Source of truth: Page::MAX_DEPTH and Page::MAX_PINNED_PAGES (app/models/page.rb)
export const DEFAULT_MAX_TREE_DEPTH = 5;
export const DEFAULT_MAX_PINNED_PAGES = 5;

interface PagesResponseData {
  success?: boolean;
  latestPagesUpdatedAt?: string | null;
  accountUsage?: AccountUsage;
}

function syncAccountUsage(accountUsage?: AccountUsage): void {
  if (!accountUsage) return;

  useAccountLimitsStore.getState().setAccountUsage(accountUsage);
}

export const usePagesStore = create<PagesStore>((set, get) => {
  // Reload every already-fetched tree page (not just page 1) so delete/move/pin
  // after infinite scroll keep the loaded window and scroll position.
  // Sequential: each reload bumps requestId; parallel would discard the first fetch.
  const refreshPagesAfterMutation = async () => {
    if (!get().hasLoaded) return;
    await reloadLoadedTreeWindow('navigation');
    await reloadLoadedTreeWindow('non_navigation');
  };

  // Reorder can pull unloaded rows into the already-fetched window (including page 1).
  const reloadLoadedTreeWindow = async (scope: PageTreeScope) => {
    const pagination =
      scope === 'navigation' ? get().mainNavigationPagination : get().nonNavigationPagination;

    const requestId = nextTreeLoadRequestId();

    try {
      const { pages, pagination: nextPagination } = await fetchLoadedTreeWindow({
        scope,
        loadedPageCount: pagination.currentPage,
        fallbackPagination: pagination,
      });

      if (requestId !== treeLoadRequestId) return;

      if (scope === 'navigation') {
        set({
          mainNavigationFlat: pages,
          mainNavigationPagination: nextPagination,
        });
        return;
      }

      set({
        nonNavigationFlat: pages,
        nonNavigationPagination: nextPagination,
      });
    } catch (error) {
      console.error('Failed to reload page tree window:', error);
      toast.error(extractApiErrorMessage(error) ?? 'Failed to refresh page list');
    }
  };

  const updatePageWithApiCall = async (
    pageId: string,
    options: {
      operation?: string;
      params?: Record<string, unknown>;
      request?: () => Promise<{ data: PagesResponseData }>;
      editorUpdate: () => Partial<Page>;
      errorMessage: string;
      successMessage?: string;
      // reload | patch | skip — how the sidebar syncs after success
      treeSync?: 'reload' | 'patch' | 'skip';
    },
  ): Promise<boolean> => {
    const pageEditorStore = usePageEditorStore.getState();
    const currentPage = pageEditorStore.page;
    const isPageInEditor = currentPage?.id === pageId;
    const treeSync = options.treeSync ?? 'reload';

    try {
      const response = await (options.request
        ? options.request()
        : http.patch(cmsPagesEditorPath(pageId), {
            operation: options.operation,
            ...options.params,
          }));

      syncAccountUsage(response.data.accountUsage);

      const { success, latestPagesUpdatedAt } = response.data ?? {};

      if (!success) {
        toast.error('Failed to update page');
        return false;
      }

      if (options.successMessage) {
        toast.success(options.successMessage);
      }

      if (treeSync !== 'skip') {
        const localUpdates = options.editorUpdate();

        if (isPageInEditor && currentPage) {
          pageEditorStore.setPage(
            {
              ...currentPage,
              ...localUpdates,
            },
            { resetHistory: false },
          );
        }

        useNestedPageViewStore.getState().updatePage({
          id: pageId,
          ...localUpdates,
        });
        if (treeSync === 'patch' && Object.keys(localUpdates).length > 0) {
          get().updatePage(pageId, localUpdates);
        }
      }

      if (latestPagesUpdatedAt !== undefined) {
        set({ latestPagesUpdatedAt });
      }
      if (treeSync === 'reload') {
        await refreshPagesAfterMutation();
      }

      return true;
    } catch (error: unknown) {
      const axiosError = error as AxiosError<{
        errors?: string | { publishedPageLimit?: string[] };
        accountUsage?: AccountUsage;
      }>;
      syncAccountUsage(axiosError.response?.data?.accountUsage);
      const errors = axiosError.response?.data?.errors;
      const pageLimitErrors = readRecord(errors)?.publishedPageLimit;
      const pageLimitMessage = Array.isArray(pageLimitErrors)
        ? readString(pageLimitErrors[0])
        : undefined;

      if (pageLimitMessage) {
        usePlanLimitModalStore.getState().showPlanLimitModal({
          title: 'Your site is growing',
          description: pageLimitMessage,
          confirmLabel: 'Explore Plans',
          cancelLabel: 'Keep as draft',
          onConfirm: () => router.visit(cmsBillingPath()),
        });
      } else {
        toast.error(extractApiErrorMessage(error) ?? options.errorMessage);
      }

      throw error;
    }
  };

  const syncPublishedPageState = (pageId: string) => {
    const publishedState = {
      isPublished: true,
      hasUnpublishedChanges: false,
      scheduledPublishAt: null,
    } satisfies Partial<Page>;

    const pageEditorStore = usePageEditorStore.getState();
    const latestPage = pageEditorStore.page;

    if (latestPage?.id === pageId) {
      pageEditorStore.setPage(
        {
          ...latestPage,
          ...publishedState,
        },
        { resetHistory: false },
      );
    }

    useNestedPageViewStore.getState().updatePage({
      id: pageId,
      ...publishedState,
    });

    get().updatePage(pageId, publishedState);
  };

  const refreshPublishedPages = async (includePageId?: string) => {
    try {
      const publishedPages = await fetchPublishedPages('', {
        includePageId,
      });
      set({ publishedPages });
    } catch (error) {
      console.error('Failed to refresh published pages:', error);
    }
  };

  const hasIndexPageType = (pageType: string) => {
    const { indexPagesExist } = get();

    switch (pageType) {
      case PageType.BLOG_INDEX:
        return indexPagesExist.blogIndex;
      case PageType.CASE_RESULT_INDEX:
        return indexPagesExist.caseResultIndex;
      case PageType.REVIEW_INDEX:
        return indexPagesExist.reviewIndex;
      default:
        return false;
    }
  };

  return {
    mainNavigationFlat: [],
    nonNavigationFlat: [],
    mainNavigationPagination: EMPTY_TREE_PAGINATION,
    nonNavigationPagination: EMPTY_TREE_PAGINATION,
    publishedPages: [],
    indexPagesExist: EMPTY_INDEX_PAGES_EXIST,
    searchResults: null,
    isLoading: false,
    hasLoaded: false,
    latestPagesUpdatedAt: null,
    maxTreeDepth: DEFAULT_MAX_TREE_DEPTH,
    maxPinnedPages: DEFAULT_MAX_PINNED_PAGES,

    setSearchResults: (results) => {
      set({ searchResults: results });
    },

    updatePage: (pageId, updates) => {
      set((state) => {
        const updatePages = (pages: Page[]): Page[] =>
          pages.map((page) => (page.id === pageId ? { ...page, ...updates } : page));

        const updateNested = (pages: Page[]): Page[] =>
          pages.map((page) => {
            if (page.id === pageId) {
              return { ...page, ...updates };
            }
            if (page.children && page.children.length > 0) {
              return {
                ...page,
                children: updateNested(page.children),
              };
            }
            return page;
          });

        const mainNavigationFlat = updatePages(state.mainNavigationFlat);
        const nonNavigationFlat = updatePages(state.nonNavigationFlat);

        const updatePublishedList = (pages: Page[]): Page[] =>
          pages.flatMap((page) => {
            if (page.id !== pageId) {
              return [page];
            }

            const updatedPage = { ...page, ...updates };
            return updatedPage.isPublished ? [updatedPage] : [];
          });

        return {
          mainNavigationFlat,
          nonNavigationFlat,
          publishedPages: updatePublishedList(state.publishedPages),
          searchResults: state.searchResults
            ? {
                mainNavigationTree: updateNested(state.searchResults.mainNavigationTree),
                nonNavigationTree: updateNested(state.searchResults.nonNavigationTree),
              }
            : null,
        };
      });
    },

    // Tree sync after create is owned by the editor's latestPagesUpdatedAt check on mount.
    createPage: (params, options = {}) => {
      return new Promise<void>((resolve) => {
        let settled = false;
        const settle = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        router.post(
          cmsPagesEditorIndexPath(),
          {
            pageType: params.pageType,
            pageTemplateCode: params.pageTemplateCode,
            sections: params.sections,
            metadata: params.metadata,
            contentModel: params.contentModel,
          } as Parameters<typeof router.post>[1],
          {
            // https://inertiajs.com/manual-visits#state-preservation
            // POST requests preserve component state by default in Inertia.js, but we need the
            // PageEditor to unmount and remount fresh when navigating to the newly created page.
            // Using 'errors' preserves state only on validation failures, allowing the PageEditor
            // to stay mounted with error messages while avoiding stale state on success.
            preserveState: 'errors',
            onSuccess: () => {
              try {
                options.onSuccess?.();
              } finally {
                settle();
              }
            },
            onError: (errors) => {
              console.error('Failed to create page:', errors);
              options.onError?.(errors);
              settle();
            },
            onCancel: () => {
              settle();
            },
            onFinish: () => {
              options.onFinish?.();
              // Fallback in case the visit was cancelled/interrupted before onSuccess/onError fired.
              settle();
            },
          },
        );
      });
    },

    publishPage: async (pageId) => {
      // Flush any pending autosave actions before publishing to ensure all changes are saved
      try {
        await flushPendingAutosave();
        await flushPendingSiteStylesSaves();
      } catch (error) {
        console.error('Failed to flush pending changes before publish:', error);
        toast.error('Failed to save pending changes. Please try again.');
        return false;
      }

      // Capture before clearing: when a draft is published it becomes the new live version
      // with the same ID. Keeping this ID ensures the next AI turn uses the correct
      // pageVersionId instead of falling back to the stale Inertia versionId prop.
      const promotedVersionId = getAutosaveSnapshot(pageId).pageVersionId;

      const success = await updatePageWithApiCall(pageId, {
        operation: 'publish',
        editorUpdate: () => ({
          isPublished: true,
          hasUnpublishedChanges: false,
          neverPublished: false,
          scheduledPublishAt: null,
        }),
        errorMessage: 'Failed to publish page',
        successMessage: 'Page published successfully',
        treeSync: 'patch',
      });

      if (success) {
        clearQueuedAutosave();
        setAutosaveContext({ pageVersionId: promotedVersionId });
        autosaveStatusEmitter.setIdle();
        syncPublishedPageState(pageId);
        await refreshPublishedPages(pageId);
        router.reload({ only: ['site'] });
        usePageEditorStore.getState().clearPublishedHistory();
        useSiteStylesStore.getState().commitChanges();

        const publishedSeo = usePageEditorStore.getState().page?.seo;
        if (!publishedSeo?.titleTag?.trim() || !publishedSeo?.metaDescription?.trim()) {
          usePageEditorStore.getState().setGeneratingSeo(true);
        }
      }

      return success;
    },

    schedulePage: async (pageId, scheduledPublishAt, options = {}) => {
      // Flush pending autosave so the scheduled draft has the latest content.
      await flushPendingAutosave();

      await updatePageWithApiCall(pageId, {
        request: () =>
          http.post(cmsPagesEditorSchedulePath(pageId), {
            // biome-ignore lint/style/useNamingConvention: snake_case required by Rails params
            scheduled_publish_at: scheduledPublishAt,
          }),
        editorUpdate: () => ({
          scheduledPublishAt,
        }),
        errorMessage: 'Failed to schedule page',
        successMessage: options.updateExisting
          ? 'Schedule updated successfully'
          : 'Page scheduled successfully',
        treeSync: 'patch',
      });
    },

    unschedulePage: async (pageId) => {
      await updatePageWithApiCall(pageId, {
        request: () => http.delete(cmsPagesEditorSchedulePath(pageId)),
        editorUpdate: () => ({
          scheduledPublishAt: null,
        }),
        errorMessage: 'Failed to cancel schedule',
        successMessage: 'Schedule canceled',
        treeSync: 'patch',
      });
    },

    unpublishPage: async (pageId) => {
      await updatePageWithApiCall(pageId, {
        operation: 'unpublish',
        editorUpdate: () => ({ isPublished: false }),
        errorMessage: 'Failed to unpublish page',
        successMessage: 'Page unpublished successfully',
        treeSync: 'patch',
      });
    },

    movePageToNonNavigation: async (pageId) => {
      await updatePageWithApiCall(pageId, {
        operation: 'move_to_scope',
        params: { isNav: false },
        editorUpdate: () => ({ isNav: false }),
        errorMessage: 'Failed to move page to non-navigation',
      });
    },

    movePageToMainNavigation: async (pageId) => {
      await updatePageWithApiCall(pageId, {
        operation: 'move_to_scope',
        params: { isNav: true },
        editorUpdate: () => ({ isNav: true }),
        errorMessage: 'Failed to move page to main navigation',
      });
    },

    reorderPage: async ({ pageId, parentId, previousSiblingId }, nextTree) => {
      let previous:
        | {
            scope: 'navigation' | 'non_navigation';
            pages: Page[];
          }
        | undefined;

      if (nextTree) {
        const nextPages = flattenPagesTree(nextTree);
        const { mainNavigationFlat, nonNavigationFlat } = get();

        if (mainNavigationFlat.some((page) => page.id === pageId)) {
          previous = {
            scope: 'navigation',
            pages: mainNavigationFlat,
          };
          set({ mainNavigationFlat: nextPages });
        } else if (nonNavigationFlat.some((page) => page.id === pageId)) {
          previous = {
            scope: 'non_navigation',
            pages: nonNavigationFlat,
          };
          set({ nonNavigationFlat: nextPages });
        }
      }

      const restorePreviousTree = () => {
        if (previous?.scope === 'navigation') {
          set({ mainNavigationFlat: previous.pages });
        } else if (previous?.scope === 'non_navigation') {
          set({ nonNavigationFlat: previous.pages });
        }
      };

      try {
        const success = await updatePageWithApiCall(pageId, {
          operation: 'reorder',
          params: { parentId, previousSiblingId },
          editorUpdate: () => ({}),
          errorMessage: 'Failed to reorder page',
          treeSync: 'skip',
        });
        if (!success) {
          restorePreviousTree();
          return;
        }
        if (previous) {
          await reloadLoadedTreeWindow(previous.scope);
        }
      } catch (error) {
        restorePreviousTree();
        throw error;
      }
    },

    togglePin: async (pageId, isPinned) => {
      const success = await updatePageWithApiCall(pageId, {
        operation: isPinned ? 'unpin' : 'pin',
        editorUpdate: () => ({ pinned: !isPinned }),
        errorMessage: 'Failed to update pin status',
      });
      if (!success) return;

      // Pin already succeeded — refresh failures must not reject the action.
      const nestedStore = useNestedPageViewStore.getState();
      const activeView = nestedStore.activeNestedView;
      if (activeView?.isIndexPage) {
        try {
          const result = await fetchIndexPageChildren(
            activeView.pageType,
            undefined,
            undefined,
            activeView.searchQuery ?? '',
          );
          nestedStore.setActiveNestedView({
            ...activeView,
            children: result.pages,
            pagination: result.pagination,
          });
        } catch (error) {
          console.error('Failed to refresh nested page list after pin:', error);
        }
      }
    },

    deletePage: async (pageId) => {
      const pageEditorStore = usePageEditorStore.getState();
      const currentPage = pageEditorStore.page;
      const isPageInEditor = currentPage?.id === pageId;

      try {
        const response = await http.delete(cmsPagesEditorPath(pageId));

        const { success, accountUsage } = response.data;

        syncAccountUsage(accountUsage);

        if (success) {
          toast.success('Page deleted successfully');
          pageEditorStore.clearDeletedPageHistory(pageId);
          set((state) => ({
            publishedPages: state.publishedPages.filter((page) => page.id !== pageId),
          }));

          useNestedPageViewStore.getState().removePage(pageId);

          if (isPageInEditor && currentPage) {
            const state = get();
            const redirectPath = getEditorRedirectPathAfterPageDelete(currentPage, [
              ...buildPagesTree(state.mainNavigationFlat),
              ...buildPagesTree(state.nonNavigationFlat),
            ]);
            router.visit(redirectPath, { replace: true });
          }

          void refreshPagesAfterMutation();
        } else {
          toast.error('Failed to delete page');
        }
      } catch (error) {
        console.error('Failed to delete page', error);
        throw error;
      }
    },

    loadPages: async () => {
      const { isLoading, hasLoaded } = get();
      if (isLoading) return;

      // Skeleton only on first load — keep the current tree visible while refreshing
      // so create/editor sync does not flash PageTreeSkeleton.
      if (!hasLoaded) {
        set({ isLoading: true });
      }
      const requestId = nextTreeLoadRequestId();

      try {
        const {
          mainNavigationPages,
          nonNavigationPages,
          mainNavigationPagination,
          nonNavigationPagination,
          publishedPages,
          indexPagesExist,
          maxDepth,
          maxPinnedPages,
          latestPagesUpdatedAt,
          accountUsage,
        } = await fetchAllPages();

        // Stale: leave isLoading to the active request (or reset).
        if (requestId !== treeLoadRequestId) return;

        syncAccountUsage(accountUsage ?? undefined);

        set({
          mainNavigationFlat: mainNavigationPages,
          nonNavigationFlat: nonNavigationPages,
          mainNavigationPagination,
          nonNavigationPagination,
          publishedPages,
          indexPagesExist,
          maxTreeDepth: maxDepth,
          maxPinnedPages,
          latestPagesUpdatedAt,
          hasLoaded: true,
          isLoading: false,
        });
      } catch (error) {
        console.error('Failed to load pages:', error);
        if (requestId === treeLoadRequestId) {
          set({ isLoading: false });
        }
      }
    },

    loadMorePages: async (scope) => {
      const state = get();
      const pagination =
        scope === 'navigation' ? state.mainNavigationPagination : state.nonNavigationPagination;

      if (pagination.currentPage >= pagination.totalPages) return;

      const requestId = treeLoadRequestId;
      const requestedPage = pagination.currentPage + 1;

      try {
        const { pages, pagination: nextPagination } = await fetchTreePageBatch({
          scope,
          page: requestedPage,
        });

        if (requestId !== treeLoadRequestId) return;

        if (scope === 'navigation') {
          set({
            mainNavigationFlat: mergePages(get().mainNavigationFlat, pages),
            mainNavigationPagination: nextPagination,
          });
          return;
        }

        set({
          nonNavigationFlat: mergePages(get().nonNavigationFlat, pages),
          nonNavigationPagination: nextPagination,
        });
      } catch (error) {
        console.error('Failed to load more pages:', error);
        toast.error(extractApiErrorMessage(error) ?? 'Failed to load more pages');
      }
    },

    hasIndexPageType,
    reset: () => {
      nextTreeLoadRequestId();
      set({
        mainNavigationFlat: [],
        nonNavigationFlat: [],
        mainNavigationPagination: EMPTY_TREE_PAGINATION,
        nonNavigationPagination: EMPTY_TREE_PAGINATION,
        publishedPages: [],
        indexPagesExist: EMPTY_INDEX_PAGES_EXIST,
        searchResults: null,
        isLoading: false,
        hasLoaded: false,
        latestPagesUpdatedAt: null,
      });
    },
  };
});

export const usePagesActions = () =>
  usePagesStore(
    useShallow((state) => ({
      createPage: state.createPage,
      publishPage: state.publishPage,
      schedulePage: state.schedulePage,
      unschedulePage: state.unschedulePage,
      unpublishPage: state.unpublishPage,
      movePageToNonNavigation: state.movePageToNonNavigation,
      movePageToMainNavigation: state.movePageToMainNavigation,
      reorderPage: state.reorderPage,
      togglePin: state.togglePin,
      deletePage: state.deletePage,
      loadPages: state.loadPages,
      loadMorePages: state.loadMorePages,
    })),
  );

export const usePagesState = () => {
  const {
    mainNavigationFlat,
    nonNavigationFlat,
    mainNavigationPagination,
    nonNavigationPagination,
    isLoading,
    hasLoaded,
    latestPagesUpdatedAt,
    maxTreeDepth,
  } = usePagesStore(
    useShallow((state) => ({
      mainNavigationFlat: state.mainNavigationFlat,
      nonNavigationFlat: state.nonNavigationFlat,
      mainNavigationPagination: state.mainNavigationPagination,
      nonNavigationPagination: state.nonNavigationPagination,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
      latestPagesUpdatedAt: state.latestPagesUpdatedAt,
      maxTreeDepth: state.maxTreeDepth,
    })),
  );

  const mainNavigationTree = useMemo(
    () => buildPagesTree(mainNavigationFlat),
    [mainNavigationFlat],
  );
  const nonNavigationTree = useMemo(() => buildPagesTree(nonNavigationFlat), [nonNavigationFlat]);

  return {
    mainNavigationTree,
    nonNavigationTree,
    mainNavigationPagination,
    nonNavigationPagination,
    isLoading,
    hasLoaded,
    latestPagesUpdatedAt,
    maxTreeDepth,
  };
};

export const usePublishedPagesState = () => {
  return usePagesStore(
    useShallow((state) => ({
      publishedPages: state.publishedPages,
      isLoading: state.isLoading,
      hasLoaded: state.hasLoaded,
    })),
  );
};

export const getMaxTreeDepth = (): number => {
  return usePagesStore.getState().maxTreeDepth;
};
